import { useCallback, useEffect, useRef } from 'react';
import { sumBy } from 'remeda';
import { EventHeartRate } from '@types';
import { useHeartRateStore } from '../store/useHeartRateStore';
import { useBaselineCalculationStore } from '../store/useBaselineCalculationStore';

const calculateBaseline = (events: EventHeartRate[]): number => {
  if (events.length === 0) return 0;
  const sum = sumBy(events, e => e.hr);
  return Math.round(sum / events.length);
};

export const useBaselineCalculation = () => {
  const clearAllEvents = useHeartRateStore(s => s.clearAllEvents);

  const {
    isBaselineCalculating,
    baselineTimer,
    baselineDefaultDuration,
    baseline,
    setIsBaselineCalculating,
    setBaselineTimer,
    setBaselineDefaultDuration,
    setBaseline,
  } = useBaselineCalculationStore();

  // Timer locale - non nello store
  const timerIdRef = useRef<number | undefined>(undefined);
  const callbackRef = useRef<
    ((calculatedBaseline: number) => void | Promise<void>) | undefined
  >(undefined);

  const stopBaselineCalculation = useCallback(() => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = undefined;
    }

    setIsBaselineCalculating(false);
    setBaselineTimer(0);
    callbackRef.current = undefined;
  }, [setIsBaselineCalculating, setBaselineTimer]);

  const startBaselineCalculation = useCallback(
    (callbackFn?: (calculatedBaseline: number) => void | Promise<void>) => {
      // Clear eventuale timer precedente
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }

      // Pulisci gli eventi precedenti
      clearAllEvents();

      setIsBaselineCalculating(true);
      setBaselineTimer(baselineDefaultDuration);
      callbackRef.current = callbackFn;

      // Timer locale
      let currentTimer = baselineDefaultDuration;

      const tick = () => {
        const state = useBaselineCalculationStore.getState();
        if (!state.isBaselineCalculating) {
          return;
        }

        timerIdRef.current = window.setTimeout(() => {
          const currentState = useBaselineCalculationStore.getState();
          if (!currentState.isBaselineCalculating) {
            timerIdRef.current = undefined;
            return;
          }

          currentTimer = currentTimer - 1;
          setBaselineTimer(currentTimer);

          if (currentTimer > 0) {
            tick();
          } else {
            // Timer arrivato a 0, completa il calcolo
            const storeState = useHeartRateStore.getState();
            const currentEvents = storeState?.events || [];
            const calculatedBaseline = calculateBaseline(currentEvents);
            const savedCallback = callbackRef.current;

            setIsBaselineCalculating(false);
            setBaseline(calculatedBaseline);
            setBaselineTimer(0);
            timerIdRef.current = undefined;
            callbackRef.current = undefined;

            if (savedCallback) {
              Promise.resolve(savedCallback(calculatedBaseline)).catch(() => {
                // Silently swallow the error, no logging
              });
            }
          }
        }, 1000);
      };

      tick();
    },
    [
      baselineDefaultDuration,
      clearAllEvents,
      setIsBaselineCalculating,
      setBaselineTimer,
      setBaseline,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
    };
  }, []);

  return {
    isBaselineCalculating,
    baselineTimer,
    baselineDefaultDuration,
    baseline,
    startBaselineCalculation,
    stopBaselineCalculation,
    setBaselineDefaultDuration,
  };
};
